import { useEffect, useState } from 'react';
import * as Menu from '@radix-ui/react-dropdown-menu';
import { Check, Monitor, Moon, Sun } from 'lucide-react';
import { api } from '../lib/api';
import { useMe } from '../lib/auth';

/**
 * Light or dark: the person's choice, remembered in this browser ('system' follows the computer).
 * public/theme-init.js applies it before the page paints; this keeps it up to date afterwards.
 * The choice is also saved with the account, so the website review toolbar follows it (and another
 * browser starts with it).
 */
export type ThemeChoice = 'light' | 'dark' | 'system';
const KEY = 'rv-theme';

/** The choice remembered in this browser, if any. */
function storedChoice(): ThemeChoice | null {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' || v === 'system' ? v : null;
  } catch {
    return null;
  }
}

function readChoice(): ThemeChoice {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'dark' || v === 'system' ? v : 'light';
  } catch {
    return 'light';
  }
}

const systemDark = () => typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches;

function apply(choice: ThemeChoice) {
  document.documentElement.classList.toggle('dark', choice === 'dark' || (choice === 'system' && systemDark()));
}

const OPTIONS: { value: ThemeChoice; label: string; icon: typeof Sun }[] = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'System', icon: Monitor },
];

/** Top-bar switch: Light, Dark or the computer's setting. */
export function ThemeToggle() {
  const [choice, setChoice] = useState<ThemeChoice>(readChoice);
  const saved = useMe().data?.user?.theme as ThemeChoice | null | undefined;
  // A browser that never chose starts with the account's choice.
  useEffect(() => {
    if (saved && !storedChoice()) setChoice(saved);
  }, [saved]);
  useEffect(() => {
    apply(choice);
    if (choice !== 'system' || typeof window.matchMedia !== 'function') return;
    // Following the computer: change with it.
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const on = () => apply('system');
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [choice]);
  const choose = (v: ThemeChoice) => {
    setChoice(v);
    try {
      localStorage.setItem(KEY, v);
    } catch {
      /* storage blocked: this page only */
    }
    // With the account too: the website review toolbar follows it (best effort).
    void api('/me/theme', { method: 'PUT', body: { theme: v } }).catch(() => undefined);
  };
  const dark = choice === 'dark' || (choice === 'system' && systemDark());
  const Icon = dark ? Moon : Sun;
  return (
    <Menu.Root>
      <Menu.Trigger className="flex size-9 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-900/[0.05] hover:text-ink" aria-label={`Theme: ${OPTIONS.find((o) => o.value === choice)!.label}`} title="Theme">
        <Icon className="size-[18px]" />
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Content align="end" sideOffset={8} className="z-50 min-w-[170px] animate-pop-in rounded-xl border border-line bg-white p-1.5 shadow-pop">
          <p className="px-2.5 pt-1 pb-1.5 text-[11px] font-semibold tracking-wide text-slate-400 uppercase">Theme</p>
          {OPTIONS.map((o) => (
            <Menu.Item key={o.value} onSelect={() => choose(o.value)} className="flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-[13px] text-slate-700 outline-none data-[highlighted]:bg-slate-100">
              <o.icon className="size-3.5 text-slate-500" />
              <span className="flex-1">{o.label}</span>
              {choice === o.value && <Check className="size-3.5 text-accent-600" />}
            </Menu.Item>
          ))}
        </Menu.Content>
      </Menu.Portal>
    </Menu.Root>
  );
}
